import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  LocalShipping as DeliveryIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Build as BuildIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

import { getRepairOrder, updateRepairOrderStatus } from '../redux/actions/repairOrderActions';

const RepairOrderDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Redux state
  const { currentRepairOrder, loading, error } = useSelector(state => state.repairOrders);
  const { token } = useSelector(state => state.auth);

  // Local state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  // Load repair order details
  useEffect(() => {
    if (id) {
      dispatch(getRepairOrder(id));
    }
  }, [dispatch, id]);

  const handleStatusUpdate = () => {
    setStatusDialogOpen(true);
    setNewStatus(currentRepairOrder?.status || '');
    setStatusNotes(currentRepairOrder?.notes || '');
  };

  const handleStatusSubmit = async () => {
    if (!newStatus || !id) return;
    
    console.log('Updating repair order status:', { id, newStatus, statusNotes });
    
    try {
      const statusData = {
        status: newStatus,
        notes: statusNotes
      };
      
      console.log('Dispatching updateRepairOrderStatus with:', statusData);
      const result = await dispatch(updateRepairOrderStatus(id, statusData));
      console.log('Update result:', result);
      
      if (result.success) {
        // Refresh the repair order data to show updated status
        dispatch(getRepairOrder(id));
        setStatusDialogOpen(false);
        setNewStatus('');
        setStatusNotes('');
      } else {
        console.error('Failed to update status:', result.message);
        // You could add a toast notification here
      }
    } catch (error) {
      console.error('Error updating status:', error);
      // You could add a toast notification here
    }
  };

  const handleGenerateReceipt = () => {
    if (!currentRepairOrder) return;
    
    // Generate receipt URL - use the API base URL directly
    const baseUrl = import.meta.env.REACT_APP_API_URL || 'https://jewel-posapi.appsinlive.in/api';
    const receiptUrl = `${baseUrl}/repair-orders/${currentRepairOrder.id}/receipt`;
    
    // Add auth token to URL for authentication - check both Redux state and localStorage
    const authToken = token || localStorage.getItem('token');
    const finalUrl = authToken ? `${receiptUrl}?token=${authToken}` : receiptUrl;
    
    console.log('Generating receipt URL:', finalUrl);
    window.open(finalUrl, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'delivered': return 'primary';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <ScheduleIcon />;
      case 'in_progress': return <EditIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'delivered': return <DeliveryIcon />;
      case 'cancelled': return <CancelIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const safeFormatCurrency = (value) => {
    const num = Number(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const calculatePaidAmount = (payments) => {
    return payments?.reduce((sum, payment) => sum + Number(payment.amount || 0), 0) || 0;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !currentRepairOrder) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Repair order not found'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/repair-orders')}
        >
          Back to Repair Orders
        </Button>
      </Box>
    );
  }

  const paidAmount = calculatePaidAmount(currentRepairOrder.payments);
  const estimatedCost = Number(currentRepairOrder.estimated_cost) || 0;
  const balance = estimatedCost - paidAmount;

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/repair-orders')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Repair Order Details
          </Typography>
          <Chip
            icon={getStatusIcon(currentRepairOrder.status)}
            label={currentRepairOrder.status?.replace('_', ' ').toUpperCase()}
            color={getStatusColor(currentRepairOrder.status)}
            size="medium"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/repair-orders/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={handleGenerateReceipt}
          >
            Generate Receipt
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Order Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Order Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Order Number</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {currentRepairOrder.order_number}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Created Date</Typography>
                  <Typography variant="body1">
                    {new Date(currentRepairOrder.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Item Type</Typography>
                  <Typography variant="body1">
                    {currentRepairOrder.item_type || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Repair Type</Typography>
                  <Typography variant="body1">
                    {currentRepairOrder.repair_type || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Item Description</Typography>
                  <Typography variant="body1">
                    {currentRepairOrder.item_description}
                  </Typography>
                </Grid>
                {currentRepairOrder.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">
                      {currentRepairOrder.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Financial Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
                  <Typography variant="h6" color="primary">
                    ₹{safeFormatCurrency(currentRepairOrder.estimated_cost)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Advance Paid</Typography>
                  <Typography variant="h6" color="success.main">
                    ₹{safeFormatCurrency(paidAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Balance Amount</Typography>
                  <Typography variant="h6" color={balance > 0 ? 'warning.main' : 'success.main'}>
                    ₹{safeFormatCurrency(balance)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payments */}
          {currentRepairOrder.payments && currentRepairOrder.payments.length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Payment History
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Payment Mode</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentRepairOrder.payments.map((payment, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {payment.paymentMode?.name || payment.payment_mode_code}
                          </TableCell>
                          <TableCell align="right">
                            ₹{safeFormatCurrency(payment.amount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status}
                              color={payment.status === 'active' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Customer Information */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Customer Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={currentRepairOrder.customer?.name}
                    secondary="Customer Name"
                  />
                </ListItem>
                {currentRepairOrder.customer?.phone && (
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={currentRepairOrder.customer.phone}
                      secondary="Phone Number"
                    />
                  </ListItem>
                )}
                {currentRepairOrder.customer?.email && (
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={currentRepairOrder.customer.email}
                      secondary="Email Address"
                    />
                  </ListItem>
                )}
                {currentRepairOrder.customer?.address && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={currentRepairOrder.customer.address}
                      secondary="Address"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Delivery Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <DeliveryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Delivery Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={currentRepairOrder.delivery_date ? 
                      new Date(currentRepairOrder.delivery_date).toLocaleDateString() : 
                      'Not set'
                    }
                    secondary="Expected Delivery Date"
                  />
                </ListItem>
                {currentRepairOrder.delivery && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={new Date(currentRepairOrder.delivery.delivered_date).toLocaleDateString()}
                        secondary="Actual Delivery Date"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={currentRepairOrder.delivery.delivered_to}
                        secondary="Delivered To"
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Repair Order Status</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>New Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  label="New Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleStatusSubmit} 
            variant="contained" 
            disabled={!newStatus || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepairOrderDetails;
